// Shared, pure helpers for StudentMarksheet2 - StudentMarksheet6.
// Grade thresholds live in ONE place (GRADE_SCALE) and are used for both
// the grade calculation and the printed legend, so they can never disagree.

export const GRADE_SCALE = [
  { grade: "A1", min: 91, max: 100 },
  { grade: "A2", min: 86, max: 90 },
  { grade: "B1", min: 76, max: 85 },
  { grade: "B2", min: 71, max: 75 },
  { grade: "C1", min: 61, max: 70 },
  { grade: "C2", min: 51, max: 60 },
  { grade: "D", min: 31, max: 50 },
  { grade: "E", min: 0, max: 30 },
];

export const getGrade = (percentage) => {
  const found = GRADE_SCALE.find((g) => percentage >= g.min);
  return found ? found.grade : "E";
};

export const getDivision = (percentage) => {
  if (percentage >= 60) return "First";
  if (percentage >= 50) return "Second";
  if (percentage >= 33) return "Third";
  return "Fail";
};

export const formatPercent = (p) => `${(Number(p) || 0).toFixed(2)}%`;

const CLASSES = [
  "NUR.",
  "LKG.",
  "UKG.",
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "11 ARTS",
  "XII ARTS",
];

export const nextClass = (current) => {
  const index = CLASSES.indexOf(current);
  if (index === -1 || index === CLASSES.length - 1) return "";
  return CLASSES[index + 1];
};

const REMARKS = {
  outstanding: [
    "Outstanding performance",
    "Excellent performance. Keep it up",
    "Awesome performance",
  ],
  good: [
    "Has shown significant progress",
    "Is a keen learner",
    "Has shown growth in academics",
    "Satisfactory performance",
    "Has a lot of potential",
  ],
  average: [
    "Is capable of achieving more",
    "Needs supervision",
    "Needs guidance",
    "Has a lot of potential",
  ],
  low: [
    "Can do much better",
    "Requires more attention",
    "Is capable of achieving more",
  ],
};

// Deterministic: the same student always gets the same remark
// (the old version used Math.random(), so it changed on every render).
export const getRemark = (percentage, seed = 0) => {
  const list =
    percentage >= 90
      ? REMARKS.outstanding
      : percentage >= 80
        ? REMARKS.good
        : percentage >= 70
          ? REMARKS.average
          : REMARKS.low;
  return list[Math.abs(Number(seed) || 0) % list.length];
};

const num = (v) => Number(v || 0);

// Builds everything a template needs from the API row.
// Grand totals are taken from each exam's subjects_detail, exactly like
// StudentMarksheet1, so numbers match across templates.
export const buildMarksheet = (data, batch) => {
  const series = data?.student_info?.series || [];
  const subjects = batch?.course?.subjects_list || [];

  const rows = subjects.map((subject) => {
    const marks = series.map((exam) => {
      const detail = exam?.tests?.[0]?.subjects_detail?.find(
        (s) => Number(s.id) === Number(subject?.id),
      );
      return {
        obtained: num(detail?.obtained_marks),
        max: num(detail?.marks),
      };
    });
    const obtained = marks.reduce((s, m) => s + m.obtained, 0);
    const max = marks.reduce((s, m) => s + m.max, 0);
    const percentage = max ? (obtained / max) * 100 : 0;

    return {
      id: subject?.id,
      name: subject?.name,
      marks,
      obtained,
      max,
      percentage,
      grade: getGrade(percentage),
    };
  });

  const seriesTotals = series.map((exam) => {
    const list = exam?.tests?.[0]?.subjects_detail || [];
    return {
      name: exam?.series_name,
      obtained: list.reduce((s, x) => s + num(x?.obtained_marks), 0),
      max: list.reduce((s, x) => s + num(x?.marks), 0),
    };
  });

  const obtained = seriesTotals.reduce((s, t) => s + t.obtained, 0);
  const total = seriesTotals.reduce((s, t) => s + t.max, 0);
  const percentage = total ? (obtained / total) * 100 : 0;

  return {
    series,
    rows,
    seriesTotals,
    overall: {
      obtained,
      total,
      percentage,
      grade: getGrade(percentage),
      division: getDivision(percentage),
    },
  };
};

export const getSummaryItems = (o) => [
  { label: "Total", value: `${o.obtained} / ${o.total}` },
  { label: "Percentage", value: formatPercent(o.percentage) },
  { label: "Grade", value: o.grade },
  { label: "Division", value: o.division },
  { label: "Result", value: o.division === "Fail" ? "Fail" : "Pass" },
];

// Not available in the API data: left blank to be filled by hand.
export const FILL_IN_ITEMS = ["Rank", "Attendance", "Discipline"];
